import React, {Suspense} from "react";
import BoughtTogetherPortal from "./BoughtTogetherPortal";
import RecommendationsPortal, {getPACustomerId} from "./RecommendationsPortal";
import SimilarProductsPortal from "./SimilarProductsPortal";
import {Customer, IItemContent} from "@connox/typescript-library";
import {getShopId} from "../../lib/getShopId";
import * as Sentry from "@sentry/browser";

type PaReturnType = {
  payload?: string[];
  message?: string;
  code?: number;
};
type ApiResultType = {result: IItemContent[]};

export const websiteIds: {[key: number]: string} = {
  100001: "c63a1643-49de-eb11-aae9-02dca44cceec",
  100009: "d3368e00-4ade-eb11-aae9-02dca44cceec",
  100017: "29d8b914-4ade-eb11-aae9-02dca44cceec",
  100027: "19e928a0-49de-eb11-aae9-02dca44cceec",
  100005: "a315dae0-49de-eb11-aae9-02dca44cceec",
  100020: "3f695d64-49de-eb11-aae9-02dca44cceec",
  100008: "6b691482-49de-eb11-aae9-02dca44cceec",
  100022: "59e7442d-4ade-eb11-aae9-02dca44cceec",
  100029: "d89451c1-49de-eb11-aae9-02dca44cceec",
};

export const paMainUrl = `https://recs-us-e1a.particularaudience.com/1.0/ClientRecom/${getWebsiteId()}/`;

export function getWebsiteId() {
  return websiteIds[getShopId()];
}

/**
 * Gets itemIds to show from PA, then gets the item data per itemIds from our GSS.
 * Item data is with customer related prices
 * @param url PA endpoint and params
 * @param customer relevant for call to our GSS, to get individual prices
 */
export async function retrieveItemContent(url: string, customer: Customer): Promise<IItemContent[]> {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  return await fetch(url, headers)
    .then((paResponse) => paResponse.json() as Promise<PaReturnType>)
    .then(async (paData) => {
      if (!paData.payload) {
        if (__DEVELOPMENT__) {
          console.log(`No PA response payload on '${url}': ${paData.message ?? paData.code ?? "no message or code"}`);
        }

        return [];
      }
      const {customerGroupId, customerId} = customer;
      const customerParams = [`currencyCode=${window.c_ActiveCurrency}`];

      if (customerId) {
        customerParams.push(`customerId=${customerId}`);
      }

      if (customerGroupId) {
        customerParams.push(`customerGroupId=${customerGroupId}`);
      }

      const customerParamsString = customerParams.join("&");
      const firstItemIds = paData.payload.slice(0, 8);
      const itemIds = firstItemIds.join(",");
      const internalUrl = `${__API_URL__}/${getShopId()}/items?view=content&itemIds=${itemIds}&${customerParamsString}`;

      if (customer.token) {
        headers.Authorization = `Bearer ${customer.token}`;
      }

      try {
        const response = await fetch(internalUrl, {
          method: "GET",
          headers,
        }).then((responseStream) => responseStream.json() as Promise<ApiResultType>);
        const sortedArray: IItemContent[] = [];
        // Sort items by the order of the itemIds in pa response
        for (const firstItemId of firstItemIds) {
          const itemData: IItemContent | undefined = response.result.find(
            (item: IItemContent): boolean => `${item.itemId}` === firstItemId,
          );
          if (itemData) {
            sortedArray.push(itemData);
          }
        }

        return sortedArray;
      } catch (internalError) {
        if (__DEVELOPMENT__) {
          console.log("DEVELOPMENT:");
          console.log(url);
          console.log(internalUrl);
          console.log(internalError);
        }

        return [];
      }
    })
    .catch((paResponseError: Error) => {
      if (__DEVELOPMENT__) {
        console.log("DEVELOPMENT:");
        console.log(url);
        console.log(paResponseError);
      }

      return [];
    });
}

export const retrieveBoughtTogether = async (itemId: string, customer: Customer) => {
  // https://docs.particularaudience.com/api-definitions/collaborative-filtering-jaccard-items-bought-together
  let paUrl = `${paMainUrl}BoughtTogether?productRef=${itemId}&filterOutOfStock=false`;
  let itemContent = await retrieveItemContent(paUrl, customer);
  if (!itemContent.length) {
    const paCustomerId = getPACustomerId();
    if (paCustomerId) {
      // https://docs.particularaudience.com/api-definitions/items-often-bought-together-recently-viewed-items
      paUrl = `${paMainUrl}BoughtTogetherRecentlyViewed?customerId=${paCustomerId}&filterOutOfStock=false`;
      itemContent = await retrieveItemContent(paUrl, customer);
    }
  }

  return itemContent;
};

const CrossSelling: React.FC = () => {
  const itemId = document.body.querySelector<HTMLElement>("#AddToCartButtonRoot")?.dataset.itemid;

  return (
    <Suspense fallback={null}>
      {/* Keep in mind that the order is determined by the order of the corresponding <section>s in the .xsl markup */}
      <BoughtTogetherPortal itemId={itemId} />
      <SimilarProductsPortal itemId={itemId} />
      <RecommendationsPortal />
    </Suspense>
  );
};

export default CrossSelling;
